import set from "lodash/set";
import TriggerEmitter, { BatchKey } from "./utils/TriggerEmitter";
import { dataTreeEvaluator } from "../handlers/evalTree";
import unset from "lodash/unset";
import { isBoolean, debounce, throttle } from "lodash";
import showAlert from "./showAlert";

function storeFnDescriptor(
  key: string,
  value: string,
  persist = true,
  switchValue: string,
  executeCondition: string,
  triggerSetting: string,
  debounceAndThrottleTime: number,
) {
  return {
    type: "STORE_VALUE" as const,
    payload: {
      key,
      value,
      persist,
      switchValue,
      executeCondition,
      triggerSetting,
      debounceAndThrottleTime,
    },
  };
}

export type TStoreValueArgs = Parameters<typeof storeFnDescriptor>;
export type TStoreValueDescription = ReturnType<typeof storeFnDescriptor>;
export type TStoreValueActionType = TStoreValueDescription["type"];

export async function storeValue(
  this: any,
  key: string,
  value: string,
  persist = true,
  switchValue: string,
  executeCondition: string,
  triggerSetting: string,
  debounceAndThrottleTime: number,
) {
  const evalTree = dataTreeEvaluator?.getEvalTree();
  const path = ["appsmith", "store", key];
  const fn = () => {
    if (evalTree) set(evalTree, path, value);
    set(this, path, value);
    TriggerEmitter.emit(
      BatchKey.process_store_updates,
      storeFnDescriptor(
        key,
        value,
        persist,
        switchValue,
        executeCondition,
        triggerSetting,
        debounceAndThrottleTime,
      ),
    );
  };
  fn();
  return {};
}

function removeValueFnDescriptor(
  key: string,
  switchValue: string,
  executeCondition: string,
  triggerSetting: string,
  debounceAndThrottleTime: number,
) {
  return {
    type: "REMOVE_VALUE" as const,
    payload: {
      key,
      switchValue,
      executeCondition,
      triggerSetting,
      debounceAndThrottleTime,
    },
  };
}

export type TRemoveValueArgs = Parameters<typeof removeValueFnDescriptor>;
export type TRemoveValueDescription = ReturnType<
  typeof removeValueFnDescriptor
>;
export type TRemoveValueActionType = TRemoveValueDescription["type"];

export async function removeValue(
  this: any,
  key: string,
  switchValue: string,
  executeCondition: string,
  triggerSetting: string,
  debounceAndThrottleTime: number,
) {
  const evalTree = dataTreeEvaluator?.getEvalTree();
  const path = ["appsmith", "store", key];
  if (evalTree) unset(evalTree, path);
  unset(this, path);
  TriggerEmitter.emit(
    BatchKey.process_store_updates,
    removeValueFnDescriptor(
      key,
      switchValue,
      executeCondition,
      triggerSetting,
      debounceAndThrottleTime,
    ),
  );
  return {};
}

function clearStoreFnDescriptor(
  switchValue: string,
  executeCondition: string,
  triggerSetting: string,
  debounceAndThrottleTime: number,
) {
  return {
    type: "CLEAR_STORE" as const,
    payload: {
      switchValue,
      executeCondition,
      triggerSetting,
      debounceAndThrottleTime,
    },
  };
}

export type TClearStoreArgs = Parameters<typeof clearStoreFnDescriptor>;
export type TClearStoreDescription = ReturnType<typeof clearStoreFnDescriptor>;
export type TClearStoreActionType = TClearStoreDescription["type"];

export async function clearStore(
  this: any,
  switchValue: string,
  executeCondition: string,
  triggerSetting: string,
  debounceAndThrottleTime: number,
) {
  const evalTree = dataTreeEvaluator?.getEvalTree();
  if (evalTree) set(evalTree, ["appsmith", "store"], {});
  this.appsmith.store = {};
  TriggerEmitter.emit(
    BatchKey.process_store_updates,
    clearStoreFnDescriptor(
      switchValue,
      executeCondition,
      triggerSetting,
      debounceAndThrottleTime,
    ),
  );
  return {};
}
